import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import {
  LeadFormPagePropTypes,
  LeadFormPageDefaultProps,
  UnifiedLeadFormCustomConfigPropTypes,
} from 'site-modules/shared/components/lead-form/lead-form-prop-types/lead-form-prop-types';
import { LeadFormWrapper } from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper';
import { LeadZipForm } from 'site-modules/shared/components/lead-form/unified-lead-form/components/lead-zip-form/lead-zip-form';
import { mergeDefaultValuesToCustomConfig } from 'site-modules/shared/components/lead-form/utils/custom-config-util';
import { ErrorState } from 'site-modules/shared/components/lead-form/error-state/error-state';

import { LeadFormEngagementHandler } from 'client/engagement-handlers/lead-form-engagement-handler/lead-form-engagement-handler';
import { EngagementHandler } from 'client/engagement-handlers';

EngagementHandler.register(LeadFormEngagementHandler);

export function UnifiedLeadForm({
  vin,
  zipCode,
  radius = LeadFormPageDefaultProps.radius,
  make = LeadFormPageDefaultProps.make,
  model = LeadFormPageDefaultProps.model,
  submodel = LeadFormPageDefaultProps.submodel,
  submodelIdentifier = LeadFormPageDefaultProps.submodelIdentifier,
  styleId = LeadFormPageDefaultProps.styleId,
  trim = LeadFormPageDefaultProps.trim,
  specialIdentifier = LeadFormPageDefaultProps.specialIdentifier,
  bodyType,
  year,
  dealerName,
  wrapperClassName = null,
  pathname = LeadFormPageDefaultProps.pathname,
  customConfig = {},
  config = LEAD_FORM_CONFIGS.UNIFIED,
  leadTargets = LeadFormPageDefaultProps.leadTargets,
  closeDrawer = null,
  updateStyle = null,
  showZipForm = false,
  leadZipFormComponent = LeadZipForm,
  zipFormHeaderCopy = undefined,
}) {
  const [isZipFormVisible, toggleZipForm] = useToggle(showZipForm);

  // merge default props
  const {
    loadingComponent: LoadingComponent,
    leadFormComponent: LeadFormComponent,
    thankYouComponent: ThankYouComponent,
    emptyLeadFormComponent: EmptyLeadFormComponent,
    ...customConfigProps
  } = mergeDefaultValuesToCustomConfig(customConfig);
  const LeadZipFormComponent = leadZipFormComponent;

  return (
    <ErrorBoundary Fallback={ErrorState}>
      {isZipFormVisible ? (
        <LeadZipFormComponent
          toggleZipForm={toggleZipForm}
          creativeId={customConfigProps.creativeId}
          customConfig={customConfigProps.leadFormConfig}
          isZipFormVisible={isZipFormVisible}
          headerCopy={zipFormHeaderCopy}
        />
      ) : (
        <LeadFormWrapper
          config={config}
          leadTargets={leadTargets}
          loadingComponent={LoadingComponent}
          leadFormComponent={LeadFormComponent}
          thankYouComponent={ThankYouComponent}
          emptyLeadFormComponent={EmptyLeadFormComponent}
          pathname={pathname}
          vin={vin}
          zipCode={zipCode}
          radius={radius || undefined}
          make={make}
          model={model}
          submodel={submodel}
          submodelIdentifier={submodelIdentifier}
          dealerName={dealerName}
          styleId={styleId}
          trim={trim}
          specialIdentifier={specialIdentifier}
          bodyType={bodyType}
          year={year}
          customConfig={customConfigProps}
          className={classnames('unified-lead-form', wrapperClassName)}
          closeDrawer={closeDrawer}
          updateStyle={updateStyle}
        />
      )}
    </ErrorBoundary>
  );
}

UnifiedLeadForm.propTypes = {
  ...LeadFormPagePropTypes,
  customConfig: PropTypes.shape(UnifiedLeadFormCustomConfigPropTypes),
  config: PropTypes.shape({}),
  wrapperClassName: PropTypes.string,
  closeDrawer: PropTypes.func,
  updateStyle: PropTypes.func,
  showZipForm: PropTypes.bool,
  leadZipFormComponent: PropTypes.elementType,
  zipFormHeaderCopy: PropTypes.string,
};
